import { CommonModule } from '@angular/common';
import { Component, computed, effect, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NgxSonnerToaster } from 'ngx-sonner';
import { DialogComponent } from './@core/components/ui/dialog/dialog.component';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IUser } from './@core/models/model';
import { AuthenticationService } from './@core/services/authentication.service';
import { SubscriptionStateService } from './@core/services/subscription-state.service';
import { hasBillingAccess } from './@core/utils/permissions.utils';
import { BlurScreenService } from './@core/services/blur-screen.service';
import { CLIENT_EVENTS, SOCKET_EVENTS, SocketService } from './@core/services/socket.service';
import { ToastService } from './@core/services/toast.service';
import { ButtonComponent } from './@core/components/ui/button/button.component';
import { SUBSCRIPTION_STATUS } from './@core/models/enums';
import { SystemMaintenanceStatusComponent } from './@shared/components/system-maintenance-status/system-maintenance-status.component';
import { BannerComponent } from './@shared/components/banner/banner.component';
import { TruncatePipe } from "./@shared/pipes/truncate.pipe";
import { TextCasePipe } from './@shared/pipes/textCase.pipe';
import { PhoneNumberDialogComponent } from './@shared/components/phone-number-dialog/phone-number-dialog.component';
import { LinkDocumentService } from './contract-management/services/link-document.service';
import { INotification, NOTIFICATION_EVENTS } from './contract-management/services/notification.service';
import { ScreenRecordingService } from './@shared/services/screen-recording.service';
import { SupportTicketDialogService } from './@shared/services/support-ticket-dialog.service';
import { environment } from '../environments/environment.dev';
import { PlanService } from './@core/services/plan.service';
import { ChatSidebarComponent } from './@shared/components/chat-sidebar/chat-sidebar.component';
import { ChatSidebarService } from './features/chat/services/chat-sidebar.service';
import { UserCreditsStateService } from './features/credits/services/user-credits-state.service';

/**
 * Root application component
 * Manages global state, socket connections, and notifications
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    NgxSonnerToaster,
    TooltipModule,
    DialogComponent,
    ButtonComponent,
    TextCasePipe,
    SystemMaintenanceStatusComponent,
    PhoneNumberDialogComponent,
    ChatSidebarComponent,
    BannerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'arena-ai-frontend';
  currentUser!: IUser | null | undefined;
  userUpdateSubscription!: Subscription
  userDeletedSubscription!: Subscription
  organizationDeletedSubscription!: Subscription
  notificationSubscription: Subscription
  subscriptionUpdatedSubscription?: Subscription;
  recordingCompletedSubscription?: Subscription;
  routerSubscription?: Subscription;
  blurScreen: boolean = false;
  ENV = environment;
  subscriptionDialogVisible = signal<boolean>(false);
  currentRoute = signal<string>('');
  showPhoneNumberDialog = false;

  isChatSidebarOpen = computed(() => this.chatSidebarService.isOpen());

  constructor(
    private authenticationService: AuthenticationService,
    private blurScreenService: BlurScreenService,
    private router: Router,
    private socketService: SocketService,
    private toastService: ToastService,
    private authService: AuthenticationService,
    private linkDocumentService: LinkDocumentService,
    private screenRecordingService: ScreenRecordingService,
    private supportTicketDialogService: SupportTicketDialogService,
    public subscriptionState: SubscriptionStateService,
    private planService: PlanService,
    private chatSidebarService: ChatSidebarService,
    private userCreditsState: UserCreditsStateService,
  ) {

    window.addEventListener("storage", (event) => {
      if (event.key === "token") {
        window.location.reload();
      }
    });


    // ✅ Fetch subscription details on sign-in (always refresh for new user, skip on page refresh for same user)
    this.authService.currentUser.subscribe(res => {
      this.currentUser = res
      this.emitSocketEvent(CLIENT_EVENTS.JOIN_ORG_ROOM)
      this.emitSocketEvent(CLIENT_EVENTS.JOIN_USER_ROOM)

      // Register rooms for automatic reconnection
      if (res) {
        const orgId = res.organization as string ?? '';
        const userId = res._id as string ?? '';
        this.socketService.registerRoom('org_room', CLIENT_EVENTS.JOIN_ORG_ROOM, orgId);
        this.socketService.registerRoom('user_room', CLIENT_EVENTS.JOIN_USER_ROOM, userId);

        if (!res.anonymous) {
          // Check if subscription belongs to current user
          const subscriptionBelongsToUser = this.subscriptionState.belongsToUser(userId);
          const hasSubscriptionData = !!this.subscriptionState.subscriptionDetails();

          // Always fetch fresh subscription details if:
          // 1. Subscription doesn't belong to current user (different user signed in)
          // 2. No subscription data exists (first load or page refresh without cached data)
          // Skip fetch only if: subscription belongs to current user AND data exists (page refresh scenario)
          if (!subscriptionBelongsToUser || !hasSubscriptionData) {
            const reason = !subscriptionBelongsToUser && hasSubscriptionData
              ? ' (new user sign-in)'
              : ' (no cached data)';
            console.log(`📦 App Component: Fetching subscription details${reason}`);
            this.subscriptionState.refreshSubscriptionDetails(userId).subscribe({
              next: () => {
                console.log('✅ App Component: Subscription loaded');
              },
              error: (err) => {
                console.error('❌ App Component: Error loading subscription:', err);
              }
            });
          } else {
            console.log('✅ App Component: Subscription already loaded (page refresh - same user)');
          }
        }

        // Fetch user credits on sign-in (always refresh to get latest remaining credits)
        this.userCreditsState.refreshCredits().subscribe({
          error: (err) => {
            console.error('❌ App Component: Error loading user credits:', err);
          }
        });

      } else {
        this.userCreditsState.clearCredits();
      }
    })

    this.userUpdateSubscription = this.socketService.on<{ token: string; message?: string }>(SOCKET_EVENTS.USER_UPDATED).subscribe(res => {
      if (!this.currentUser) {
        return;
      }
      this.authService.setUserData(res[0])
      if (res[0].message) {
        this.toastService.success(res[0].message);
      }
      this.blurScreenService.enableBlur();
      setTimeout(() => {
        window.location.reload();
      }, 1000)
    })

    this.userDeletedSubscription = this.socketService.on<{ message: string }>(SOCKET_EVENTS.USER_DELETED).subscribe(res => {
      this.toastService.error(res[0].message || 'Your account has been deleted.');
      this.blurScreenService.enableBlur();
      setTimeout(() => {
        this.blurScreenService.disableBlur();
        this.authService.logout();
      }, 2000)
    })

    this.organizationDeletedSubscription = this.socketService.on<{ message: string }>(SOCKET_EVENTS.ORGANIZATION_DELETED).subscribe(res => {
      this.toastService.error(res[0].message || 'Your organization has been deleted.');
      this.blurScreenService.enableBlur();
      setTimeout(() => {
        this.blurScreenService.disableBlur();
        this.authService.logout();
      }, 2000)
    })

    this.subscriptionUpdatedSubscription = this.socketService.on<{ organization: string }>(SOCKET_EVENTS.SUBSCRIPTION_UPDATED).subscribe(() => {
      this.subscriptionState.refreshSubscriptionDetails().subscribe();
      this.subscriptionState.socketRefresh$.next();
    });

    this.notificationSubscription = this.socketService.on<{ notification: INotification }>(SOCKET_EVENTS.NOTIFICATION).subscribe(res => {
      switch (res[0].notification.subModule) {
        case NOTIFICATION_EVENTS.DOCUMENT.GENERATED:
          const notification = res[0].notification.event.data as { contractName: string, contractId: string };
          this.linkDocumentService.reloadLinkedFiles.next(null);
          this.toastService.success(
            `${notification.contractName} has been generated successfully`,
            'Click here to redirect to document!',
            () => {
              this.router.navigate(['/edit-document'], { queryParams: { documentId: notification.contractId } }).then(() => location.reload());
            }
          );
          break;
        default:
      }
    })

    effect(() => {
      // ✅ Read currentRoute FIRST to ensure it's always tracked as a dependency
      // This ensures the effect re-runs whenever the route changes, even if we exit early
      const currentUrl = this.currentRoute();

      // ✅ STEP 1: Check if user is authenticated (exclude anonymous users)
      if (!this.currentUser || this.currentUser.anonymous) {
        this.subscriptionDialogVisible.set(false);
        return;
      }

      // ✅ STEP 2: Check if subscription data is loaded
      const subscriptionDetails = this.subscriptionState.subscriptionDetails();
      if (!subscriptionDetails) {
        this.subscriptionDialogVisible.set(false);
        return;
      }

      // ✅ STEP 3: Don't show on auth routes
      const authRoutes = ['/login', '/signup', '/forgot-password', '/reset-password', '/onboard', '/verify', '/invitation-signup', '/email-auth'];
      const isAuthRoute = authRoutes.some(route => currentUrl.startsWith(route));

      if (isAuthRoute) {
        this.subscriptionDialogVisible.set(false);
        return;
      }

      // ✅ STEP 4: Check subscription status
      const isSubscriptionEnded = this.subscriptionState.isSubscriptionEnded();

      if (currentUrl.startsWith('/pricing')) {
        if (isSubscriptionEnded && !this.subscriptionState.isSidebarLocked()) {
          this.subscriptionState.lockSidebar();
        }
        return;
      }

      this.subscriptionDialogVisible.set(isSubscriptionEnded);

      if (isSubscriptionEnded) {
        this.router.navigate(['/explore']);
        return;
      }
    })
  }

  ngOnInit(): void {
    // PrimeNG 18: Configuration is now handled via providePrimeNG in app.config.ts
    window.addEventListener('message', this.receiveMessage.bind(this), false);

    // Initialize current route from router state
    this.currentRoute.set(this.router.routerState.snapshot.url);

    // Subscribe to router events to track URL changes (so effect re-runs on route change)
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute.set(event.urlAfterRedirects);
      });

    this.subscriptionState.showSubscriptionDialog$.subscribe(() => {
      this.subscriptionDialogVisible.set(true);
    });

    // Subscribe to blur state changes
    this.blurScreenService.getBlurState().subscribe(blurState => {
      this.blurScreen = blurState;
    });

    // Handle recording completion globally
    this.recordingCompletedSubscription = this.screenRecordingService.recordingCompleted$.subscribe(recording => {
      this.supportTicketDialogService.openDialog({
        attachment: {
          file: recording.file,
          preview: recording.videoUrl,
          isImage: false,
          isVideo: true
        }
      });
      this.screenRecordingService.resetRecording();
      this.toastService.success('Video captured successfully. Please complete the support ticket form.');
    });
  }

  ngOnDestroy(): void {
    if (this.userUpdateSubscription) {
      this.userUpdateSubscription.unsubscribe();
    }
    if (this.userDeletedSubscription) {
      this.userDeletedSubscription.unsubscribe();
    }
    if (this.organizationDeletedSubscription) {
      this.organizationDeletedSubscription.unsubscribe();
    }
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
    if (this.subscriptionUpdatedSubscription) {
      this.subscriptionUpdatedSubscription.unsubscribe();
    }
    if (this.recordingCompletedSubscription) {
      this.recordingCompletedSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    this.emitSocketEvent(CLIENT_EVENTS.LEAVE_ORG_ROOM)
    this.emitSocketEvent(CLIENT_EVENTS.LEAVE_USER_ROOM)
    // Unregister rooms
    this.socketService.unregisterRoom('org_room');
    this.socketService.unregisterRoom('user_room');
  }

  async emitSocketEvent(event: CLIENT_EVENTS) {
    const socket = await this.socketService.getSocket()
    if (!socket) {
      console.warn('Socket is not initialized, skipping emit event:', event)
      return
    }
    const payload = (event == CLIENT_EVENTS.JOIN_ORG_ROOM ? (this.currentUser?.organization as string ?? '') : (this.currentUser?._id as string ?? ''))
    socket.emit(event as CLIENT_EVENTS.JOIN_ORG_ROOM | CLIENT_EVENTS.JOIN_USER_ROOM | CLIENT_EVENTS.LEAVE_ORG_ROOM | CLIENT_EVENTS.LEAVE_USER_ROOM, payload)
  }

  receiveMessage(event: MessageEvent) {
    // Verify the origin of the data for security
    if (event.origin !== 'http://localhost:4200') return;

    if (event.data.type === 'ARENA_CONTRACT_ANALYSER') {
      const data = event.data.data;
      const user = this.authenticationService.setUserData(data);
      if (user) {
        this.router.navigate([''])
      } else {
        this.router.navigate(['/login'])
      }
    }
  }

  /**
   * Get dialog message based on subscription status
   * 
   * Messages:
   * - Trial expired without payment: "Your trial has ended!"
   * - Inactive subscription: "Your subscription is inactive!"
   * - Default: "Your subscription has ended!"
   */
  getDialogMessage(): string {
    const subscription = this.subscriptionState.subscriptions();
    const subscriptionStatus = subscription?.status;

    // Check if trial expired without payment
    const isTrialExpired = this.subscriptionState.isTrialExpired();

    if (isTrialExpired) {
      return 'Your trial has ended!';
    } else if (subscriptionStatus === SUBSCRIPTION_STATUS.INACTIVE) {
      return 'Your subscription is inactive!';
    } else {
      return 'Your subscription has ended!';
    }
  }

  /** Account owner or a delegated billing admin — may activate a plan from the subscription-ended dialog. */
  get hasBillingAccess(): boolean {
    return hasBillingAccess(this.currentUser);
  }

  talkToSales() {
    this.planService.sendUserDataToContactSales(this.subscriptionState.plan()?.name ?? '').subscribe((res: any) => {
      if (res.code === 1004) {
        this.showPhoneNumberDialog = true;
      } else if (res.success) {
        this.toastService.success('Request to sales sent successfully');
      } else {
        this.toastService.error('Failed to send request to sales');
      }
    });
  }

  onPhoneNumberSubmitted() {
    // Retry the sales contact request after phone number is updated
    this.planService.sendUserDataToContactSales(this.subscriptionState.plan()?.name ?? '').subscribe((res: any) => {
      if (res.success) {
        this.toastService.success('Request to sales sent successfully');
      }
      else {
        this.toastService.error('Failed to send request to sales');
      }
    });
  }

  /**
   * Navigate to pricing page and lock sidebar
   */
  navigateToPricing() {
    // Dismiss the dialog and lock sidebar before navigating
    this.subscriptionDialogVisible.set(false);
    this.subscriptionState.dismissSubscriptionDialog();
    this.subscriptionState.lockSidebar();
    this.router.navigate(['/pricing']);
  }

  /**
   * Handle subscription dialog visibility change event
   * When user clicks the close button, visibleChange fires with false
   * We dismiss the dialog via subscription state and update the signal
   */
  onSubscriptionDialogVisibleChange(visible: boolean) {
    if (!visible) {
      // Dialog is closing - dismiss it via subscription state
      this.subscriptionState.dismissSubscriptionDialog();
      this.subscriptionDialogVisible.set(false);
    }
  }
}
